import * as React from "react";
import {inject, observer} from "mobx-react";
import styled, {css} from "styled-components";
import type {ISwitchListChange} from "../../../../../widgets/button/switch/SwitchList";
import type {AppState} from "../../../../../../data/state/AppState";
import type {FeatureMap} from "../../../../../../data/state/AppStateConstants";
import {ArrayUtils} from "../../../../../../utils/data/array/ArrayUtils";
import type {Type} from "../../../../../../data/models/Type";
import type {TransportLayer} from "../../../../../../data/TransportLayer";
import {XyiconFeature, FieldDataType} from "../../../../../../generated/api/base";
import {ReactUtils} from "../../../../../utils/ReactUtils";
import type {ISelectSliderOption} from "../../../../../widgets/input/selectslider/SelectSlider";
import type {CardLayoutEditor} from "../../../../../modules/settings/modules/type/form/CardLayoutEditor";
import {AssignType} from "../../../../../modules/settings/modules/field/FieldForm";
import {ToggleContainerV5} from "../../../../widgets/ToggleContainerV5/ToggleContainerV5";
import {SelectSliderV5} from "../../../../details/SelectSliderV5";
import {colorPalette} from "../../../../styles/colorPalette";
import {baseDistance} from "../../../../styles/styles";
import {TypeFormHeaderV5} from "./TypeFormHeaderV5";
import {CardLayoutEditorV5} from "./CardLayoutEditorV5";

interface ITypeFormProps {
	readonly type: Type;
	readonly fieldsFeature?: XyiconFeature;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
	readonly avatar?: boolean;
	readonly createPanelNameInput?: (name: string) => void;
	readonly isCreatingType?: boolean;
}

interface ITypeFormState {
	readonly isMappingInProgress: boolean;
}

const headerComponents: FeatureMap<React.ComponentClass<{type: Type}>> = {
	[XyiconFeature.Boundary]: TypeFormHeaderV5,
	[XyiconFeature.Space]: TypeFormHeaderV5,
};

@inject("appState")
@inject("transport")
@observer
export class TypeFormV5 extends React.Component<ITypeFormProps, ITypeFormState> {
	private _cardLayoutEditorRef = React.createRef<CardLayoutEditor>();
	public _assignedTypes: ISelectSliderOption[] = [
		{
			id: AssignType.Unassigned,
			label: AssignType[AssignType.Unassigned],
		},
		{
			id: AssignType.Assigned,
			label: AssignType[AssignType.Assigned],
		},
		this.props.fieldsFeature === XyiconFeature.Xyicon && {
			id: AssignType.Lookup,
			label: "Lookup Link",
			tooltip: "Lookup Link is only applicable for Text (Single Line) fields where Display On Links is enabled.",
		},
	].filter((type) => type);

	constructor(props: ITypeFormProps) {
		super(props);

		this.state = {
			isMappingInProgress: false,
		};
	}

	private onMappingChange = async (changes: ISwitchListChange[]) => {
		const {type, fieldsFeature, appState} = this.props;

		const mapping = appState.typeFieldMapping[fieldsFeature];

		if (!mapping[type.id]) {
			mapping[type.id] = new Set();
		}
		const mappingForType = mapping[type.id];

		for (const change of changes) {
			const fieldId = change.id;

			if (change.value) {
				mappingForType.add(fieldId);
			} else {
				mappingForType.delete(fieldId);
			}
		}

		this._cardLayoutEditorRef.current?.forceUpdate();
		await this.updateMapping();
	};

	private onSliderMappingChange = async (labels: string[], value: AssignType) => {
		this.setState({isMappingInProgress: true});

		try {
			const {type, fieldsFeature, appState} = this.props;
			let list: {id: string; label: string; value: boolean}[] = [];

			type.settings.xyiconLookupLinkFieldList = type.settings.xyiconLookupLinkFieldList || [];

			if (appState.fields[fieldsFeature]) {
				list = appState.actions.getFieldsForType(type.id, fieldsFeature);
			}

			labels.forEach((label) => {
				const fieldItem = list.find((field) => field.label === label);
				const fieldObj = appState.actions.getFieldById(fieldItem.id);

				if (!fieldObj || !fieldItem) {
					return;
				}

				if (value === AssignType.Lookup && fieldObj.displayOnLinks && fieldObj.dataType === FieldDataType.SingleLineText) {
					fieldItem.value = false;
					ArrayUtils.addMutable(type.settings.xyiconLookupLinkFieldList, fieldObj.id);
				} else {
					fieldItem.value = value === AssignType.Assigned;
					ArrayUtils.removeMutable(type.settings.xyiconLookupLinkFieldList, fieldObj.id);
				}
			});

			await this.props.transport.services.typefield.updateType(type);
			await this.onMappingChange(list);
		} finally {
			this.setState({isMappingInProgress: false});
		}
	};

	private updateMapping = async () => {
		const selected = this.props.type;

		if (selected) {
			const services = this.props.transport.services;

			await services.typefield.applyTypeMapping(this.props.fieldsFeature, selected.id);
		}
	};

	private getHeaderComponent() {
		const {type} = this.props;

		return headerComponents[type.feature] || TypeFormHeaderV5;
	}

	public override render() {
		const {type, fieldsFeature, appState, isCreatingType, avatar} = this.props;
		const {isMappingInProgress} = this.state;
		const editMode = !!type.id;
		const TypeFormHeaderV5 = this.getHeaderComponent();
		let list: {id: string; label: string; value: boolean}[] = [];

		if (appState.fields[fieldsFeature]) {
			list = appState.actions.getFieldsForType(type.id, fieldsFeature, true);
		}

		return (
			<>
				<TypeFormHeaderStyled
					$isCreatingType={isCreatingType}
					className={ReactUtils.cls("header", {noInitials: !avatar})}
				>
					<TypeFormHeaderV5
						type={type}
						avatar={avatar}
						createPanelNameInput={this.props.createPanelNameInput}
					/>
				</TypeFormHeaderStyled>
				{editMode && (
					<>
						<ToggleContainerV5
							title="Assigned Fields"
							open={true}
						>
							<SelectSliderV5
								options={this._assignedTypes}
								rows={list.map((f) => {
									let value = f.value ? AssignType.Assigned : AssignType.Unassigned;
									const fieldObj = appState.actions.getFieldById(f.id);
									let disabledOptionsList: ISelectSliderOption[] = [];

									if (fieldsFeature === XyiconFeature.Xyicon) {
										if (!fieldObj?.displayOnLinks || fieldObj.dataType !== FieldDataType.SingleLineText) {
											disabledOptionsList.push({id: AssignType.Lookup, label: this._assignedTypes.find((at) => at.id === AssignType.Lookup).label});
										}

										if (type.settings.xyiconLookupLinkFieldList?.includes(f.id)) {
											value = AssignType.Lookup;
										}
									}

									return {
										label: f.label,
										value,
										disabledOptionsList,
									};
								})}
								onChange={this.onSliderMappingChange}
								disabled={isMappingInProgress}
							/>
						</ToggleContainerV5>
						<ToggleContainerV5
							title="Customize Card Layout"
							open={true}
						>
							<CardLayoutEditorV5
								type={type}
								feature={fieldsFeature}
								// ref={this._cardLayoutEditorRef}
							/>
						</ToggleContainerV5>
					</>
				)}
			</>
		);
	}
}

const TypeFormHeaderStyled = styled.div<{$isCreatingType: boolean}>`
	display: flex;
	flex-direction: column;

	${(props) => {
		if (props.$isCreatingType) {
			return css`
				border-bottom: none;
				padding-bottom: 0;
				gap: ${baseDistance.sm};
			`;
		} else {
			return css`
				border-bottom: 2px solid ${colorPalette.gray.c300};
				padding-bottom: ${baseDistance.md};
				gap: ${baseDistance.md};
			`;
		}
	}}
`;
